<template>
	<div class="flex space-x-2">
		<div
			:disabled="!isFocused"
			:class="[
				boldStyles,
				'size-6 rounded-md bg-won-sand-light',
				'flex cursor-pointer items-center justify-center select-none',
			]"
			@click="toggleBold"
		>
			<svg
				class="size-4"
				viewBox="0 0 20 20"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g clip-path="url(#clip0_2448_34472)">
					<path
						d="M12.9999 8.99159C13.8083 8.43325 14.3749 7.51659 14.3749 6.66659C14.3749 4.78325 12.9166 3.33325 11.0416 3.33325H5.83325V14.9999H11.6999C13.4416 14.9999 14.7916 13.5833 14.7916 11.8416C14.7916 10.5749 14.0749 9.49159 12.9999 8.99159ZM8.33325 5.41659H10.8333C11.5249 5.41659 12.0833 5.97492 12.0833 6.66659C12.0833 7.35825 11.5249 7.91659 10.8333 7.91659H8.33325V5.41659ZM11.2499 12.9166H8.33325V10.4166H11.2499C11.9416 10.4166 12.4999 10.9749 12.4999 11.6666C12.4999 12.3583 11.9416 12.9166 11.2499 12.9166Z"
						:fill="fillColor('bold')"
					/>
				</g>
				<defs>
					<clipPath id="clip0_2448_34472">
						<rect
							width="20"
							height="20"
							fill="white"
						/>
					</clipPath>
				</defs>
			</svg>
		</div>

		<div
			:disabled="!isFocused"
			:class="[
				italicStyles,
				'size-6 rounded-md bg-won-sand-light',
				'flex cursor-pointer items-center justify-center select-none',
			]"
			@click="toggleItalic"
		>
			<svg
				class="size-4"
				viewBox="0 0 20 20"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g clip-path="url(#clip0_2448_34474)">
					<path
						d="M8.33333 3.33325V5.83325H10.175L7.325 12.4999H5V14.9999H11.6667V12.4999H9.825L12.675 5.83325H15V3.33325H8.33333Z"
						:fill="fillColor('italic')"
					/>
				</g>
				<defs>
					<clipPath id="clip0_2448_34474">
						<rect
							width="20"
							height="20"
							fill="white"
						/>
					</clipPath>
				</defs>
			</svg>
		</div>

		<div
			:disabled="!isFocused"
			:class="[
				underlineStyles,
				'size-6 rounded-md bg-won-sand-light',
				'flex cursor-pointer items-center justify-center select-none',
			]"
			@click="toggleUnderline"
		>
			<svg
				class="size-4"
				viewBox="0 0 20 20"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g clip-path="url(#clip0_2448_34476)">
					<path
						d="M10.0001 14.1667C12.7584 14.1667 15.0001 11.925 15.0001 9.16667V2.5H12.9167V9.16667C12.9167 10.775 11.6084 12.0833 10.0001 12.0833C8.39175 12.0833 7.08341 10.775 7.08341 9.16667V2.5H5.00008V9.16667C5.00008 11.925 7.24175 14.1667 10.0001 14.1667ZM4.16675 15.8333V17.5H15.8334V15.8333H4.16675Z"
						:fill="fillColor('underline')"
					/>
				</g>
				<defs>
					<clipPath id="clip0_2448_34476">
						<rect
							width="20"
							height="20"
							fill="white"
						/>
					</clipPath>
				</defs>
			</svg>
		</div>

		<div class="relative flex space-x-2">
			<div
				:class="[
					'won-tag-light-gray size-6 rounded-md',
					'flex cursor-pointer items-center justify-center select-none',
				]"
				@click="handleShowColorsModal"
			>
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clip-path="url(#clip0_1_25283)">
						<path
							d="M4.57501 14.1667H6.59168L7.65001 11.1833H12.3583L13.4083 14.1667H15.425L11.0417 2.5H8.95835L4.57501 14.1667ZM8.25835 9.49167L9.95001 4.66667H10.05L11.7417 9.49167H8.25835Z"
							:fill="fillLinkColor"
						/>
						<path
							d="M18.3334 16.667H1.66669V20.0003H18.3334V16.667Z"
							:fill="fillBorderBottomWithSelectedColor"
						/>
					</g>
					<defs>
						<clipPath id="clip0_1_25283">
							<rect
								width="20"
								height="20"
								fill="white"
							/>
						</clipPath>
					</defs>
				</svg>
			</div>

			<div
				v-if="visibleColorModal"
				v-click-outside="handleClickOutside"
				class="
					absolute inset-x-0 z-40 mt-6 flex w-[145px] flex-wrap items-center
					justify-start rounded border border-won-gray-light bg-[#FFFFFF]
					p-1 shadow-lg
				"
			>
				<div
					v-for="color in colors"
					:key="color"
					:style="[
						{ backgroundColor: color },
						[editor.isActive('textStyle', { color: color }) ? {'--tw-ring-color': color} : null]
					]"
					:class="['', { [`ring-1 ring-offset-1`]: editor.isActive('textStyle', { color: color }) }]"
					class="mt-1.5 ml-1.5 flex size-[20px] cursor-pointer items-center justify-center rounded-full"
					@click="() => handleSetColor(color)"
				>
					<svg
						v-if="editor.isActive('textStyle', { color })"
						width="10"
						height="8"
						viewBox="0 0 10 8"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M3.50002 6.08504L1.41502 4.00004L0.705017 4.70504L3.50002 7.50004L9.50002 1.50004L8.79502 0.795044L3.50002 6.08504Z"
							fill="#F9FBFA"
						/>
					</svg>
				</div>

				<div
					class="
						mt-1.5 ml-1.5 inline-flex size-[20px] cursor-pointer items-center
						justify-center rounded-full bg-[#EFF6FF] ring-1 ring-[#5282EB]
						ring-offset-1
					"
					@click="triggerColor"
				>
					<svg
						width="10"
						height="10"
						viewBox="0 0 10 10"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M7.77778 5.00005H2.22223"
							stroke="#5283EB"
							stroke-linecap="round"
						/>
						<path
							d="M4.99999 2.22229V7.77785"
							stroke="#5283EB"
							stroke-linecap="round"
						/>
					</svg>
				</div>
				<input
					ref="color"
					type="color"
					class="invisible size-0"
					:value="editor ? editor.getAttributes('textStyle').color : null"
					@input="e => handleSetColor(e.target.value)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useTiptapStore } from '../../store/tiptap.store';
import { checkIfEmpty, rgbToHex } from '../../utils/helpers';

const tiptap = useTiptapStore();
const { editor, isFocused } = storeToRefs(tiptap);

const color = ref(null);
const visibleColorModal = ref(false);

const colors = ['#000000', '#515151', '#17296F', '#4E96D8', '#255514', '#63349F', '#8F3821', '#EA917D', '#DFA93B'];
const fillColor = (button) => isFocused.value && editor.value.isActive(button) ? '#2A8176' : '#444C58';

const boldStyles = computed(() => ({
	'bg-[#E5F2EA]': isFocused.value && editor.value.isActive('bold')
}));

const italicStyles = computed(() => ({
	'bg-[#E5F2EA]': isFocused.value && editor.value.isActive('italic')
}));

const underlineStyles = computed(() => ({
	'bg-[#E5F2EA]': isFocused.value && editor.value.isActive('underline')
}));

const isSelectedText = () => {
	const currentSelectionIsEmpty = editor.value ? editor.value.view.state.selection.empty : true;
	if (!currentSelectionIsEmpty) {return true;}
	return false;
};

const toggleBold = () => {
	isSelectedText() && (editor.value && editor.value.chain().focus().toggleBold().run());
};
const toggleItalic = () => {
	isSelectedText() && (editor.value && editor.value.chain().focus().toggleItalic().run());
};
const toggleUnderline = () => {
	isSelectedText() && (editor.value && editor.value.chain().focus().toggleUnderline().run());
};

const triggerColor = () => {
	color.value.click();
};

const handleShowColorsModal = () => {
	const currentSelectionIsEmpty = editor.value ? editor.value.view.state.selection.empty : true;
	if (currentSelectionIsEmpty) {return;}

	// selected default color
	const selectedColor = editor.value.getAttributes('textStyle').color;
	if (selectedColor && selectedColor.match(/(rgb)+/g)) {
		const [r,g,b] = selectedColor.match(/\d+/gm);
		const selectedHexColor = rgbToHex(parseInt(r),parseInt(g),parseInt(b));
		handleSetColor(selectedHexColor.toUpperCase());
	}

	visibleColorModal.value = true;
};
const handleHideColorsModal = () => visibleColorModal.value = false;
const handleClickOutside = () => {
	handleHideColorsModal();
};

const handleSetColor = (color) => {
	editor.value.chain().focus().setColor(color).run();
};

const fillBorderBottomWithSelectedColor = computed(() => {
	const color = editor.value ? editor.value.getAttributes('textStyle').color : null;
	return isFocused.value && editor.value ? (checkIfEmpty(color) ? '#333333' : color) : '#333333';
});

const fillLinkColor = computed(() => visibleColorModal.value === true ? '#2A8176' : '#444C58');
</script>
